"use client";

/**
 * Хук для загрузки account-level переименований колонок из БД.
 *
 * Возвращает Map<key, label>. Используется в страницах таблиц/видов чтобы
 * отобразить имена заданные админом через `/admin/columns`. Кэшируется на
 * уровне модуля — один fetch на загрузку страницы, все потребители видят
 * одинаковые данные без дублирования запросов.
 */

import { useEffect, useState } from "react";
import { apiFetchWithWarmup } from "@/lib/api/browser-warmup";

let cachedLabels: Map<string, string> | null = null;
let pendingFetch: Promise<Map<string, string>> | null = null;

async function fetchLabelsOnce(): Promise<Map<string, string>> {
  if (cachedLabels) return cachedLabels;
  if (pendingFetch) return pendingFetch;
  pendingFetch = (async () => {
    try {
      const data = await apiFetchWithWarmup<{ labels: Record<string, string> }>("/api/column-labels");
      const map = new Map(Object.entries(data.labels ?? {}));
      cachedLabels = map;
      return map;
    } catch {
      return new Map();
    } finally {
      pendingFetch = null;
    }
  })();
  return pendingFetch;
}

export function preloadColumnLabelsClient() {
  void fetchLabelsOnce();
}

/** Принудительная инвалидация — после редактирования в админке. */
export function invalidateColumnLabelsClient() {
  cachedLabels = null;
}

export function useColumnLabels(): Map<string, string> {
  const [labels, setLabels] = useState<Map<string, string>>(() => cachedLabels ?? new Map());
  useEffect(() => {
    if (cachedLabels) { setLabels(cachedLabels); return; }
    fetchLabelsOnce().then(m => setLabels(m));
  }, []);
  return labels;
}
